import { Graphics, Container, ContainerChild, Text } from "pixi.js";

import type {
  IStudioWidget,
  IStudioWidgetWeather,
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import {
  describeWeather,
  fetchWeather,
} from "../../../../services/OpenMeteoService";

/**
 * Broadcast (Pixi) renderer for the WEATHER widget. Mirrors the React
 * `WeatherWidget` "custom" (free-style) render: a main temperature line plus an
 * optional secondary "city · condition" line, driven live by Open-Meteo. The
 * OSR mounts only the Pixi composition (no React widgets), so this renderer owns
 * its own polling loop — one `fetchWeather` per widget on the configured cadence
 * (≥5 min, like the React widget), updating the Pixi `Text` in place.
 *
 * Baked SVG `theme`s (dark/news/y2k/…) are React-only; here we always render the
 * plain text form (the themed cards would need their own SVG→texture path, TODO).
 * The scene templates use `tempOnly` (degree-only) so this is exact for them.
 */

const MIN_POLL_MS = 5 * 60 * 1000;
const DEFAULT_POLL_MS = 15 * 60 * 1000;

interface WeatherRenderState {
  timer: ReturnType<typeof setInterval> | null;
  abort: AbortController | null;
}

export class WeatherWidgetRenderer {
  private states = new Map<string, WeatherRenderState>();

  render(
    widget: IStudioWidget,
    container: Container,
    destination: { x: number; y: number; width: number; height: number },
  ): void {
    const options = (widget as IStudioWidgetWeather).options ?? {};

    // Tear down any previous polling loop for this widget id before re-rendering
    // (the factory re-runs render() on every studio update).
    this.cleanupWidget(widget.id);

    const backgroundColor = options.backgroundColor || "transparent";
    if (backgroundColor !== "transparent") {
      const bg = new Graphics();
      bg.rect(0, 0, destination.width, destination.height).fill(
        backgroundColor,
      );
      container.addChild(bg);
    }

    // Clip to bounds so a long condition line never spills out of the widget.
    const mask = new Graphics();
    mask.rect(0, 0, destination.width, destination.height).fill(0xffffff);
    container.addChild(mask);
    container.mask = mask;
    container.cullable = true;

    const fontSize = options.fontSize ? parseInt(options.fontSize) : 32;
    const fontFamily = options.fontFamily || "Roboto";
    const color = options.fontColor || "#ffffff";
    const fontWeight = options.bold ? "bold" : "normal";
    const fontStyle = options.italic ? "italic" : "normal";
    const textAlign = options.textAlign || "left";
    const tempOnly = options.tempOnly === true;

    const anchorX = textAlign === "left" ? 0 : textAlign === "right" ? 1 : 0.5;
    const lineX =
      textAlign === "left"
        ? 0
        : textAlign === "right"
          ? destination.width
          : destination.width / 2;

    const mainText = new Text({
      text: "—°",
      style: {
        fontFamily,
        fontSize,
        fill: color,
        align: textAlign,
        fontWeight,
        fontStyle,
        wordWrapWidth: destination.width,
      },
    });
    container.addChild(mainText as unknown as ContainerChild);

    const subText = tempOnly
      ? null
      : new Text({
          text: "",
          style: {
            fontFamily,
            fontSize: Math.max(10, Math.round(fontSize * 0.4)),
            fill: color,
            align: textAlign,
            fontWeight,
            fontStyle,
            wordWrapWidth: destination.width,
          },
        });
    if (subText) {
      subText.alpha = 0.85;
      container.addChild(subText as unknown as ContainerChild);
    }

    // Vertically center the (1 or 2 line) group inside the widget bounds.
    const gap = subText ? Math.round(fontSize * 0.1) : 0;
    const layout = () => {
      const total = mainText.height + (subText ? gap + subText.height : 0);
      const startY = (destination.height - total) / 2;
      mainText.anchor.set(anchorX, 0);
      mainText.position.set(lineX, startY);
      if (subText) {
        subText.anchor.set(anchorX, 0);
        subText.position.set(lineX, startY + mainText.height + gap);
      }
    };
    layout();

    const lat = options.latitude;
    const lon = options.longitude;
    if (lat === undefined || lon === undefined) {
      // No location configured — match the React empty state.
      mainText.text = "—";
      if (subText) subText.text = "Configurá una ciudad";
      layout();
      return;
    }

    const units = options.units ?? "celsius";
    const pollMs = Math.max(
      MIN_POLL_MS,
      options.pollIntervalMs ?? DEFAULT_POLL_MS,
    );

    const state: WeatherRenderState = { timer: null, abort: null };
    this.states.set(widget.id, state);

    const run = async () => {
      state.abort?.abort();
      const ctrl = new AbortController();
      state.abort = ctrl;
      try {
        const forecast = await fetchWeather(
          {
            latitude: lat,
            longitude: lon,
            timezone: options.timezone,
            units,
            forecastDays: 1,
          },
          ctrl.signal,
        );
        if (ctrl.signal.aborted) return;
        // Defensive: never write to Pixi objects that a re-render/scene-change
        // already destroyed. The factory destroys the old container then calls
        // render()→cleanupWidget()→abort() synchronously, so `aborted` already
        // covers the race — but this guard makes it robust to ordering changes.
        if (mainText.destroyed) return;
        const temp = Math.round(forecast.current.temperature);
        mainText.text = tempOnly
          ? `${temp}°`
          : `${temp}°${forecast.units === "fahrenheit" ? "F" : "C"}`;
        if (subText) {
          const meta = describeWeather(
            forecast.current.weatherCode,
            "es",
            forecast.current.isDay,
          );
          subText.text = options.displayName
            ? `${options.displayName} · ${meta.label}`
            : meta.label;
        }
        layout();
      } catch {
        // Network/abort error — keep the last shown value (or the placeholder).
      }
    };

    void run();
    state.timer = setInterval(() => void run(), pollMs);
  }

  private cleanupWidget(widgetId: string): void {
    const state = this.states.get(widgetId);
    if (!state) return;
    if (state.timer) clearInterval(state.timer);
    state.abort?.abort();
    this.states.delete(widgetId);
  }

  public destroy(): void {
    for (const state of this.states.values()) {
      if (state.timer) clearInterval(state.timer);
      state.abort?.abort();
    }
    this.states.clear();
  }
}
